/**
 * HealthInsuranceRow — one row of the health-insurance list.
 *
 * Flutter parity:
 *   - `health_insurance_widget.dart` (read view)
 *   - `edit_health_insurance_widget.dart` (inline edit form)
 *
 * The edit form mirrors the Flutter field order:
 *   1. Insurance card photos (front / back)
 *   2. Insurance provider (typeahead)
 *   3. Member ID
 *   4. Eligibility start / Expiration dates
 *   5. Co-pay type toggle + per-billing-group breakdown
 *   6. Confirmation checkbox (verbatim copy)
 *
 * Card photos + co-pay breakdown were deferred in V1 (F-PAT-243); now
 * built using the shared `PayerPhotoUploader` + `PayerCopayTable`
 * (also used by the scheduling appointment-insurance editor). Wire
 * shapes confirmed against the 2026-06-05 /api/Patients/HealthInsurance
 * and /api/Master/UploadImage dev curls.
 */

import { useState } from 'react';
import { Pencil, Trash2, X, Search } from 'lucide-react';
import { RecordRowCard } from '@/shared/ui/components/record-row-card';
import { Button, FieldLabel, StatusChip } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { DatePickerField } from '@/shared/ui/components';
import { Switch } from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useSearchInsurancePayers } from '@/patients/services/use-search-insurance-payers';
import { PayerPhotoUploader } from '@/shared/ui/components/payer-photo-uploader';
import { PayerCopayTable } from '@/shared/ui/components/payer-copay-table';
import { IMAGE_AREAS } from '@/services/common/use-upload-image';
import { cn } from '@/lib/utils';
import {
  PayerReadLines,
  firstTpa,
  PayerSearchOption,
  payerTitle,
} from '@/patients/lib/payer-read-lines';
import type {
  HealthInsurance,
  PayerBillingGroup,
} from '@/services/scheduling/use-prebook-selected-patient';
import type { BillingGroup } from '@/shared/types/billing';

/** Lightweight UI flag layered on the wire type — never sent. */
export interface InsuranceRow extends HealthInsurance {
  isEdit?: boolean;
}

export interface HealthInsuranceRowProps {
  row: InsuranceRow;
  /** True when this is the only non-deleted row (Remove disabled). */
  isOnly: boolean;
  /** Tenant context for the provider search endpoint. */
  organizationId: string;
  /** Org billing groups for the co-pay breakdown table. */
  billingGroups: BillingGroup[];
  /** Org currency code for the "Fixed, {code}" co-pay pill. */
  currencyCode: string;
  readOnly?: boolean;
  onChange: (next: InsuranceRow) => void;
  onCancel: () => void;
  onRemove: () => void;
}

export function HealthInsuranceRowComponent({
  row,
  isOnly,
  organizationId,
  billingGroups,
  currencyCode,
  readOnly,
  onChange,
  onCancel,
  onRemove,
}: HealthInsuranceRowProps) {
  if (row.isDeleted) return null;
  return row.isEdit ? (
    <EditCard
      row={row}
      isOnly={isOnly}
      organizationId={organizationId}
      billingGroups={billingGroups}
      currencyCode={currencyCode}
      readOnly={readOnly}
      onChange={onChange}
      onCancel={onCancel}
      onRemove={onRemove}
    />
  ) : (
    <ReadCard
      row={row}
      isOnly={isOnly}
      readOnly={readOnly}
      onEdit={() => onChange({ ...row, isEdit: true })}
      onRemove={onRemove}
    />
  );
}

function ReadCard({
  row,
  isOnly,
  readOnly,
  onEdit,
  onRemove,
}: {
  row: InsuranceRow;
  isOnly: boolean;
  readOnly?: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const provider = row.insuranceProvider;
  const tpa = firstTpa(provider?.relatedTpa);
  // TPA-mediated payers show "{TPA companyName} ({payerName})" (Flutter
  // dropdown-title rule, CLAUDE.md visual rules); direct payers show payerName.
  const title = provider ? payerTitle(provider) : 'Unknown provider';
  // The approved design's PaRecordRowCard: the payer with its Primary / Secondary and Verified chips, the policy facts
  // as the quiet lines, edit and remove on the right. Same buttons, handlers and disabled rule as before.
  return (
    <RecordRowCard
      title={title}
      chips={
        <>
          <StatusChip semantic="info" tone="soft">
            {row.isPrimary ? 'Primary' : 'Secondary'}
          </StatusChip>
          {row.isVerified ? (
            <StatusChip semantic="success" tone="soft">
              Verified
            </StatusChip>
          ) : null}
        </>
      }
      lines={
        <PayerReadLines
          policyNumber={row.policyNumber}
          payerCompanyId={provider?.payerCompanyId}
          tpaCode={tpa?.code}
          eligibilityStart={row.eligiblityStartDate}
          expirationDate={row.expirationDate}
          expiredLabel="Insurance Expired"
        />
      }
      actions={
        !readOnly ? (
          <>
            <button type="button" onClick={onEdit} className="icon-action" aria-label="Edit insurance">
              <Pencil className="size-4" />
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={isOnly}
              className={cn('icon-action', isOnly && 'opacity-40 cursor-not-allowed hover:bg-transparent')}
              aria-label="Remove insurance"
            >
              <Trash2 className="size-4" />
            </button>
          </>
        ) : null
      }
    />
  );
}

function EditCard({
  row,
  isOnly,
  organizationId,
  billingGroups,
  currencyCode,
  readOnly,
  onChange,
  onCancel,
  onRemove,
}: {
  row: InsuranceRow;
  isOnly: boolean;
  organizationId: string;
  billingGroups: BillingGroup[];
  currencyCode: string;
  readOnly?: boolean;
  onChange: (next: InsuranceRow) => void;
  onCancel: () => void;
  onRemove: () => void;
}) {
  const update = (patch: Partial<InsuranceRow>) => onChange({ ...row, ...patch });

  // Provider typeahead. Uses `useSearchInsurancePayers` (Samer PR #91
  // review): it returns the full `PayerDetails` shape including
  // `payerCompanyId`, `address`, `phoneNumber`, `relatedTpa[]`, etc.
  // — all required by the save endpoint. The earlier `useInsurers`
  // lookup returned only `{id, name}`, so newly-picked providers
  // produced an incomplete `insuranceProvider` object that 4xx'd on save.
  const [insurerQuery, setInsurerQuery] = useState(
    row.insuranceProvider ? payerTitle(row.insuranceProvider) : '',
  );
  const debouncedQuery = useDebounce(insurerQuery, 250);
  const { data: insurers } = useSearchInsurancePayers({
    organizationId,
    query: debouncedQuery,
  });
  const [insurerOpen, setInsurerOpen] = useState(false);

  // Co-pay validation (Flutter `edit_health_insurance_widget.dart` onSave):
  // selecting billing groups requires a co-pay type AND a value on each.
  const selectedGroups = row.billingGroups ?? [];
  const copayValid =
    selectedGroups.length === 0 ||
    (!!row.copaymentType && selectedGroups.every((g) => !!g.value));

  const canDone =
    !!row.insuranceProvider?.id &&
    !!row.policyNumber?.trim() &&
    !!row.expirationDate &&
    copayValid;

  return (
    // The row opened into its fields (design PaRecordRowCard `editing`): Done (primary) then Remove (tertiary).
    <RecordRowCard
      editing
      title={row.healthInsuranceId ? 'Edit insurance' : 'New insurance'}
      actions={
        <button type="button" onClick={onCancel} className="icon-action" aria-label="Cancel edit">
          <X className="size-4" />
        </button>
      }
      editActions={
        <>
          <Button type="button" size="sm" onClick={() => update({ isEdit: false })} disabled={readOnly || !canDone}>
            Done
          </Button>
          <Button type="button" variant="tertiary" size="sm" onClick={onRemove} disabled={isOnly || readOnly}>
            Remove
          </Button>
        </>
      }
    >

      {/* Card photos — Flutter field order puts these first
          (edit_health_insurance_widget.dart). Area = PATIENT
          (Flutter `imageAreaPatient`; 2026-06-05 UploadImage curl). */}
      <div>
        <FieldLabel >
          Insurance card photos
        </FieldLabel>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <PayerPhotoUploader
            url={row.frontPhotoUrl ?? null}
            label="Front Photo"
            side="front"
            titleNoun="insurance"
            area={IMAGE_AREAS.PATIENT}
            disabled={readOnly}
            onUploaded={(url) => update({ frontPhotoUrl: url })}
            onClear={() => update({ frontPhotoUrl: null })}
          />
          <PayerPhotoUploader
            url={row.backPhotoUrl ?? null}
            label="Back Photo"
            side="back"
            titleNoun="insurance"
            area={IMAGE_AREAS.PATIENT}
            disabled={readOnly}
            onUploaded={(url) => update({ backPhotoUrl: url })}
            onClear={() => update({ backPhotoUrl: null })}
          />
        </div>
      </div>

      <div>
        <FieldLabel >
          Insurance provider <span className="text-status-error">*</span>
        </FieldLabel>
        <div className="relative mt-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-hint" />
          <TextField
            value={insurerQuery}
            onChange={(e) => {
              setInsurerQuery(e.target.value);
              setInsurerOpen(true);
            }}
            onFocus={() => setInsurerOpen(true)}
            onBlur={() => setTimeout(() => setInsurerOpen(false), 150)}
            placeholder="Start typing an insurance provider…"
            disabled={readOnly}
            inputClassName="pl-8"
          />
          {insurerOpen && (insurers?.length ?? 0) > 0 ? (
            <ul className="absolute left-0 right-0 top-full mt-1 z-10 max-h-72 overflow-auto scrollbar-hide rounded-md border border-border-default bg-surface-card shadow-md">
              {(insurers ?? []).slice(0, 12).map((p) => (
                <li key={p.id ?? p.payerName ?? ''}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      // Ship the FULL PayerDetails shape — the save endpoint
                      // requires payerCompanyId, payerAgreementId, relatedTpa[],
                      // etc. all present (verified via 2026-05-14 curl).
                      update({
                        insuranceProviderId: p.id,
                        insuranceProvider: p,
                      });
                      setInsurerQuery(payerTitle(p));
                      setInsurerOpen(false);
                    }}
                    className="block w-full px-3 py-2 text-left hover:bg-grey-100"
                  >
                    <PayerSearchOption provider={p} />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      <div>
        <FieldLabel >
          Member ID / Policy number <span className="text-status-error">*</span>
        </FieldLabel>
        <TextField
          value={row.policyNumber ?? ''}
          onChange={(e) => update({ policyNumber: e.target.value })}
          placeholder="Enter policy number"
          maxLength={50}
          className="mt-1"
          disabled={readOnly}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel >
            Eligibility start
          </FieldLabel>
          <DatePickerField
            value={(row.eligiblityStartDate ?? '').slice(0, 10)}
            onChange={(v) =>
              update({
                eligiblityStartDate: v ? `${v}T00:00:00.000` : null,
              })
            }
            className="mt-1"
            disabled={readOnly}
          />
        </div>
        <div>
          <FieldLabel >
            Expiration <span className="text-status-error">*</span>
          </FieldLabel>
          <DatePickerField
            value={(row.expirationDate ?? '').slice(0, 10)}
            onChange={(v) =>
              update({
                expirationDate: v ? `${v}T00:00:00.000` : null,
              })
            }
            className="mt-1"
            disabled={readOnly}
          />
        </div>
      </div>

      {/* Co-pay type toggle + per-billing-group breakdown. Only when the
          org has billing groups configured (Flutter renders nothing
          otherwise). billingGroups item shape ships
          {billingGroupId, category, value, maximumCopayment} per the
          2026-06-05 HealthInsurance curl. */}
      {billingGroups.length > 0 ? (
        <PayerCopayTable<PayerBillingGroup>
          label="Co-Pay"
          showMaxCopay
          billingGroups={billingGroups}
          selected={row.billingGroups ?? []}
          onChange={(next) => update({ billingGroups: next })}
          copayType={row.copaymentType}
          onCopayTypeChange={(next) => update({ copaymentType: next })}
          currencyCode={currencyCode}
          disabled={readOnly}
          makeGroup={(g) => ({
            billingGroupId: g.id,
            category: g.category,
            value: null,
            maximumCopayment: null,
          })}
        />
      ) : null}

      <div className="flex items-center justify-between gap-3 pt-2 border-t border-border-default">
        {/* Verbatim Flutter copy — edit_health_insurance_widget.dart:652.
            Was paraphrased ("I've verified the details match the card");
            the conversion contract requires confirmation/validation strings
            be copied verbatim, not reworded (F-PAT-252). `text-sm` + flex-1
            kept from main; gap-3/flex-1 let the longer two-sentence copy wrap
            beside the Switch. */}
        {/* TODO(i18n): this is compliance-adjacent copy hardcoded to match
            Flutter verbatim. If it ever becomes legally-managed, source it
            from config/i18n so it can't drift in a compliance update. */}
        <p className="text-sm text-text-primary flex-1">
          I confirm that the data entered matches that on the card. This insurance policy is accepted at this clinic.
        </p>
        <Switch
          checked={!!row.isVerified}
          onCheckedChange={(checked) => update({ isVerified: checked })}
          disabled={readOnly}
          aria-label="I confirm that the data entered matches that on the card. This insurance policy is accepted at this clinic."
        />
      </div>

    </RecordRowCard>
  );
}
